import { Controller } from "stimulus";

export default class FixedPlayerController extends Controller {
  // #fixed-player is data-turbo-permanent, so Turbo detaches and reattaches this element on every
  // navigation and connect() runs again on the SAME instance. State that should survive a
  // navigation therefore belongs in initialize(), not connect() - resetting currentEpisodeId per
  // connect would make the next timeupdate look like a new episode and re-fetch metadata mid-play.
  initialize() {
    this.currentEpisodeId = null;

    this.onTimeUpdate = this.update.bind(this);
    this.onPlaybackEnded = this.loadNextEpisode.bind(this);
    this.onQueueChanged = this.refreshUpNextFromQueue.bind(this);
  }

  connect() {
    window.addEventListener("supercast-timeupdate", this.onTimeUpdate);
    window.addEventListener("supercast-playback-ended", this.onPlaybackEnded);
    window.addEventListener("supercast-queue-changed", this.onQueueChanged);
  }

  // Without this, each navigation would add a second copy of every listener and every event would
  // fire the handler once per navigation made.
  disconnect() {
    window.removeEventListener("supercast-timeupdate", this.onTimeUpdate);
    window.removeEventListener("supercast-playback-ended", this.onPlaybackEnded);
    window.removeEventListener("supercast-queue-changed", this.onQueueChanged);
  }

  update(event) {
    if (event.detail.mediaType != 'audio') {
      this.hide();
      return;
    }
    this.show();
    if (event.detail.itemId == this.currentEpisodeId) {
      return;
    }
    this.currentEpisodeId = event.detail.itemId;

    if (event.detail.itemType == "Episode") {
      this.fetchMetadataStream(event.detail.itemId)
        .then(() => this.refreshUpNextFromQueue())
        .catch((error) => console.error(error));
      this.setFeedTokenConnected(event.detail.itemId);
    }
  }

  show() {
    this.element.hidden = false;
  }

  hide() {
    this.element.hidden = true;
  }

  fetchMetadataStream(episodeId) {
    return fetch(`/subscriber_v2/episodes/${episodeId}/metadata${window.location.search}`, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
      }
    })
      .then(response => response.text())
      .then(html => Turbo.renderStreamMessage(html));
  }

  setFeedTokenConnected(episodeId) {
    fetch(`/subscriber_v2/feed_items/mark_feed_token_connected?episode_id=${episodeId}`, {
      method: "POST"
    });
  }

  refreshUpNextFromQueue(event) {
    const requestToken = (this.upNextRequestToken = (this.upNextRequestToken || 0) + 1);
    const episodeId = this.currentEpisodeId;
    const isStale = () => requestToken !== this.upNextRequestToken || episodeId != this.currentEpisodeId;

    // supercast-queue-changed already carries the head row; only the internal callers
    // (update/connect) have to ask the server for one. A present `detail` with a null head means
    // "the queue is empty", which is why this tests for the detail rather than for the head.
    const headPromise = event?.detail
      ? Promise.resolve(event.detail.head)
      : window.queueClient.getQueue().then((queue) => queue[0]);

    return headPromise.then((head) => {
      if (isStale()) return;

      if (!head) {
        this.upNextQueueItemId = null;
        this.upNextQueueEpisodeId = null;
        // The queue can empty before anything has played (clearing it on a fresh page load), in
        // which case there is no episode whose metadata could restore the up-next slot.
        if (!episodeId) return;

        return this.fetchMetadataStream(episodeId).catch((error) => console.error(error));
      }

      return fetch(`/subscriber_v2/episodes/${head.itemId}/queue_next`, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
        }
      })
        .then(response => {
          if (!response.ok) throw new Error(`Failed to fetch queue_next: ${response.status}`);
          return response.text();
        })
        .then(html => {
          if (isStale()) return;
          Turbo.renderStreamMessage(html);
          this.upNextQueueItemId = head.id;
          this.upNextQueueEpisodeId = head.itemId;
        })
        .catch((error) => {
          console.error(error);
          if (isStale()) return;
          this.upNextQueueItemId = null;
          this.upNextQueueEpisodeId = null;
        });
    });
  }

  dequeueUpNext(event) {
    if (this.upNextQueueItemId == null) return;
    if (!event.target.closest('[data-play-button-target="play"]')) return;

    const dequeuedId = this.upNextQueueItemId;
    this.upNextQueueItemId = null;
    this.upNextQueueEpisodeId = null;
    window.queueClient.removeFromQueue(dequeuedId).catch((error) => console.error(error));
  }

  loadNextEpisode() {
    if (this.hasNextEpisodePlayButtonTarget) {
      const playButtonElement = this.nextEpisodePlayButtonTarget.querySelector("[data-controller='play-button']");
      if (playButtonElement) {
        const playController = this.application.getControllerForElementAndIdentifier(playButtonElement, "play-button");
        if (playController) {
          playController.play();
        }
      }
    }

    if (this.upNextQueueItemId != null) {
      const dequeuedId = this.upNextQueueItemId;
      this.upNextQueueItemId = null;
      this.upNextQueueEpisodeId = null;
      window.queueClient.removeFromQueue(dequeuedId).catch((error) => console.error(error));
    }
  }
}

FixedPlayerController.targets = ['nextEpisodePlayButton'];
